/**
 * The API client base.
 *
 * A4 owns this file. It knows about transport concerns only: the prefix, cookies,
 * idempotency keys, ETags, RFC 9457 problem parsing and the retry that follows a
 * successful step-up.
 *
 * It knows nothing about how a session is obtained or how a step-up is performed. That is
 * A2's. The seam is `setAuthAdapter`: A2 registers callbacks once, at shell mount, and this
 * client calls them. Nothing else in the app talks to `fetch` directly.
 */
import type { ApiError, ErrorCode, StepUpTier } from '@elutide/shared';
import { ulid } from './ulid';

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api/v1';

export type QueryValue = string | number | boolean | null | undefined;

export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  query?: Record<string, QueryValue | QueryValue[]>;
  body?: unknown;
  signal?: AbortSignal;
  /** Reuse a key across retries of the same logical command. One is generated otherwise. */
  idempotencyKey?: string;
  /** Set by the retry path after a step-up completes. Callers do not pass this. */
  stepUpId?: string;
  headers?: Record<string, string>;
  /** Opt out of the automatic step-up retry, for probes that want the 403 verbatim. */
  noStepUpRetry?: boolean;
}

/** An RFC 9457 problem response, thrown so callers can branch on a stable `code`. */
export class ApiProblem extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'network_error';
  readonly requestId: string | null;
  readonly problem: Partial<ApiError> | null;
  readonly stepUpTier: StepUpTier | null;

  constructor(init: {
    status: number;
    code: ErrorCode | 'network_error';
    message: string;
    requestId?: string | null;
    problem?: Partial<ApiError> | null;
    stepUpTier?: StepUpTier | null;
  }) {
    super(init.message);
    this.name = 'ApiProblem';
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId ?? null;
    this.problem = init.problem ?? null;
    this.stepUpTier = init.stepUpTier ?? null;
  }

  /** Last six characters of the request id, which is what a toast shows. */
  get shortRequestId(): string | null {
    return this.requestId ? this.requestId.slice(-6) : null;
  }
}

/**
 * Implemented by A2 (`apps/web/src/features/auth`). Every callback is optional so the shell
 * runs standalone in mock mode with no auth feature present at all.
 */
export interface AuthAdapter {
  /** A step-up id already held for this tier, if one is fresh. */
  currentStepUpId?: (tier: StepUpTier) => string | null;
  /** Open the step-up dialog. Resolve with the new step-up id, or null when cancelled. */
  requestStepUp?: (tier: StepUpTier) => Promise<string | null>;
  /** 401 unauthenticated: the session is gone. Send the user to /login. */
  onUnauthenticated?: () => void;
  /** 401 session_locked: idle soft lock. Show the unlock screen without losing the route. */
  onSessionLocked?: () => void;
  /** 429. The client does not retry automatically; the surface decides. */
  onRateLimited?: (retryAfterSeconds: number | null) => void;
}

let adapter: AuthAdapter = {};

export function setAuthAdapter(next: AuthAdapter): void {
  adapter = next;
}

export function getAuthAdapter(): AuthAdapter {
  return adapter;
}

/** Test seam. Resets the adapter between cases. */
export function clearAuthAdapter(): void {
  adapter = {};
}

function buildUrl(path: string, query?: ApiRequestOptions['query']): string {
  const base = path.startsWith('/') ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    if (raw === undefined || raw === null) continue;
    if (Array.isArray(raw)) {
      const joined = raw.filter((v) => v !== undefined && v !== null).join(',');
      if (joined) params.set(key, joined);
    } else {
      params.set(key, String(raw));
    }
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function tierFromProblem(problem: Partial<ApiError> | null): StepUpTier | null {
  const first = problem?.errors?.[0];
  if (!first) return null;
  // The API emits errors[0] as { field: 'x-step-up-id', code: 'missing' | 'stale',
  // message: 'tier 3' }, so the tier is a number inside the message rather than the
  // whole of it. A bare number and a field named `tier` are both still accepted.
  const fromMessage = /(\d+)/.exec(first.message ?? '')?.[1];
  const parsed = fromMessage === undefined ? Number.NaN : Number(fromMessage);
  if (parsed === 2 || parsed === 3) return parsed;
  if (first.field === 'tier') {
    const value = Number(first.code);
    if (value === 2 || value === 3) return value;
  }
  return null;
}

async function readProblem(response: Response): Promise<Partial<ApiError> | null> {
  const type = response.headers.get('content-type') ?? '';
  if (!type.includes('json')) return null;
  try {
    return (await response.json()) as Partial<ApiError>;
  } catch {
    return null;
  }
}

const MUTATIONS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

async function once<T>(path: string, options: ApiRequestOptions): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };

  if (MUTATIONS.has(method)) {
    headers['Idempotency-Key'] = options.idempotencyKey ?? ulid();
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.stepUpId) {
    headers['X-Step-Up-Id'] = options.stepUpId;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'include',
      cache: 'no-store',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    throw new ApiProblem({
      status: 0,
      code: 'network_error',
      message: cause instanceof Error ? cause.message : 'The network request failed.',
    });
  }

  if (response.status === 204 || response.status === 304) {
    return undefined as T;
  }

  if (response.ok) {
    const type = response.headers.get('content-type') ?? '';
    if (!type.includes('json')) return undefined as T;
    return (await response.json()) as T;
  }

  const problem = await readProblem(response);
  const requestId = response.headers.get('X-Request-Id') ?? problem?.request_id ?? null;
  const code = (problem?.code ?? 'internal') as ErrorCode;

  throw new ApiProblem({
    status: response.status,
    code,
    message: problem?.detail ?? problem?.title ?? `Request failed with ${response.status}.`,
    requestId,
    problem,
    stepUpTier: tierFromProblem(problem),
  });
}

/**
 * The only way the app talks to the API.
 *
 * Behaviour on failure, per DESIGN.md section 6:
 *  - 403 step_up_required opens A2's step-up dialog and retries the original request once,
 *    reusing the same idempotency key so a completed write is never duplicated.
 *  - 401 session_locked opens the lock screen. The route is not changed.
 *  - 401 unauthenticated sends the user to login.
 * Everything else is thrown as an ApiProblem for the calling surface to render.
 */
export async function apiFetch<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const idempotencyKey = MUTATIONS.has(method)
    ? (options.idempotencyKey ?? ulid())
    : undefined;

  const withKey: ApiRequestOptions = {
    ...options,
    ...(idempotencyKey ? { idempotencyKey } : {}),
  };

  // Send a step-up id we already hold rather than provoking a 403 for it.
  if (!withKey.stepUpId && adapter.currentStepUpId) {
    const held = adapter.currentStepUpId(2) ?? adapter.currentStepUpId(3);
    if (held) withKey.stepUpId = held;
  }

  try {
    return await once<T>(path, withKey);
  } catch (error) {
    if (!(error instanceof ApiProblem)) throw error;

    if (error.code === 'session_locked') {
      adapter.onSessionLocked?.();
      throw error;
    }
    if (error.code === 'unauthenticated') {
      adapter.onUnauthenticated?.();
      throw error;
    }
    if (error.code === 'rate_limited') {
      adapter.onRateLimited?.(null);
      throw error;
    }
    if (error.code === 'step_up_required' && !options.noStepUpRetry && adapter.requestStepUp) {
      const tier: StepUpTier = error.stepUpTier ?? 2;
      const stepUpId = await adapter.requestStepUp(tier);
      if (!stepUpId) throw error;
      return once<T>(path, { ...withKey, stepUpId });
    }
    throw error;
  }
}

/** Convenience wrappers. Every mutation carries its own idempotency key. */
export const api = {
  get: <T>(path: string, options: Omit<ApiRequestOptions, 'method' | 'body'> = {}) =>
    apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  del: <T>(path: string, body?: unknown, options: ApiRequestOptions = {}) =>
    apiFetch<T>(path, { ...options, method: 'DELETE', body }),
};

/** Health lives outside /api/v1. */
export async function fetchPublicHealth<T>(): Promise<T> {
  const response = await fetch('/health', { credentials: 'omit', cache: 'no-store' });
  if (!response.ok) {
    throw new ApiProblem({
      status: response.status,
      code: 'dependency_unavailable',
      message: 'Health endpoint did not answer.',
    });
  }
  return (await response.json()) as T;
}
